import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil, toString } from 'lodash'
import classNames from 'classnames'
import { IconMessageCircle } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchPrompts, fetchModels } from '@/reducers/prompts/prompts.actions'
import {
  recordCalibrationAdopted,
  resolveWithPromptChanges,
  savePromptChangesToScorecard,
  markTaskInProgress,
} from '@/reducers/promptFeedback/promptFeedback.actions'
import { patchTask } from '@/reducers/tasks/tasks.actions'
import { COMPLETED, READY_FOR_REVIEW, COLLECTING_EVIDENCE } from '@/reducers/tasks/tasks.constants'
import { fetchScorecardsByOrg } from '@/reducers/scorecards/scorecards.actions'
import { selectSupportWindowOpenForOrg } from '@/reducers/supportAccess/supportAccess.redux'
import { setTask } from '@/reducers/tasks/tasks.redux'
import { CopyButton } from '@/components/buttons/CopyButton'
import { Banner } from '@/components/banners/Banner'
import { ExternalLink } from '@/components/ExternalLink'

import { getScorecardLookupData } from '@/utils/promptFeedback.helpers'
import { TaskComments } from '../../components/TaskComments'
import { TaskDrawerFooter } from '../TaskDrawerFooter'
import { Metadata } from '../Metadata'
import { usePromptEditing } from './hooks/usePromptEditing'
import { usePromptTesting } from './hooks/usePromptTesting'
import { EvidenceList } from './components/EvidenceList'
import { OptimizationReport } from './components/OptimizationReport'
import { PromptEditor } from './components/PromptEditor'
import { TestCaseList, TestSectionHeader, CriteriaContext } from './components/TestCaseList'

import './PromptFeedbackDrawer.scss'

/**
 * Build feedback subtitle parts for rendering as a continuous sentence:
 * {count} reports from {calls} about "{criteria}" on {scorecard} • {organization}
 */
const buildFeedbackSubtitle = (
  evidenceCount,
  uniqueCallCount,
  criteriaName,
  scorecardName,
  scorecardId,
  organizationName,
  isUnredactedOrg
) => {
  if (evidenceCount === 0) {
    return { text: '1 call for review' }
  }

  const reportWord = evidenceCount === 1 ? 'report' : 'reports'
  const callWord = uniqueCallCount === 1 ? 'call' : 'calls'
  let prefix = `${evidenceCount} ${reportWord} from ${uniqueCallCount} ${callWord}`

  if (criteriaName) {
    prefix += ` about "${criteriaName}"`
  }

  const hasScorecard = scorecardName && scorecardId
  const hasOrg = organizationName && !isUnredactedOrg

  if (hasScorecard) {
    prefix += ' on '
  } else if (hasOrg) {
    prefix += ' for '
  }

  let orgSuffix = null
  if (hasOrg && hasScorecard) {
    orgSuffix = ` • ${organizationName}`
  } else if (hasOrg) {
    orgSuffix = organizationName
  }

  return {
    prefix,
    scorecardName: hasScorecard ? scorecardName : null,
    scorecardId: hasScorecard ? scorecardId : null,
    orgSuffix,
  }
}

export const PromptFeedbackDrawer = ({
  handleClose,
  expanded = false,
  refetchTasks,
  taskIsOpen,
  handlePrimaryAction,
  handleSecondaryAction,
  primaryActionButtonText,
  secondaryActionButtonText,
  onDirtyChange,
}) => {
  const dispatch = useDispatch()
  const { task } = useSelector((state) => state.tasks)
  const {
    prompts,
    loading: promptsLoading,
    modelOptions: { qa_copilot: qaCopilotModelOptions = [] } = {},
  } = useSelector((state) => state.prompts)
  const { scorecardConfigs } = useSelector((state) => state.scorecards.data)
  const organizations = useSelector((state) => state.organizations) || []
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  // QCER-802: the support-access window must belong to the task's org — the
  // slice's `windowOrgId` keeps an Org A fetch from un-protecting Org B here.
  const supportWindowActive = useSelector(
    (state) => currentUserOrgId === 1 && selectSupportWindowOpenForOrg(state, task?.organization_id)
  )

  // Check if the task's organization has unredacted_enabled AND user is viewing from a different org
  // If user is logged into the same org as the task, they have full access.
  // Exception: a support access window grants Balto admins temporary visibility.
  const taskOrganization = organizations.find((org) => org.id === task?.organization_id)
  const isUnredactedOrg =
    taskOrganization?.unredacted_enabled === true &&
    currentUserOrgId !== task?.organization_id &&
    !supportWindowActive

  const [fetchedOrgId, setFetchedOrgId] = useState(null)
  const [savingToScorecard, setSavingToScorecard] = useState(false)

  // Hidden rows are dismissed reported calls (fetched with show_hidden_evidence).
  // Everything that means "the task's feedback" — counts, Run All, expected-score fallback —
  // works off the pending rows only, which is what makes dismissing calls shorten test runs.
  const taskEvidence = task?.evidence || []
  const allEvidence = taskEvidence.filter((evidence) => !evidence.hidden)
  const dismissedEvidence = taskEvidence.filter((evidence) => evidence.hidden)
  const evidenceCount = allEvidence.length
  const uniqueCallCount = new Set(allEvidence.map((evidence) => evidence.call_id)).size
  // Identity (prompt, scorecard) comes from ANY evidence row including dismissed ones —
  // with every call dismissed, a pending-only source would leave matchedPrompt undefined
  // and unmount the whole test section, toggle included.
  const firstEvidence = taskEvidence[0]
  const currentPromptId = firstEvidence?.current_prompt_id
  const originalFeedbackPromptId = task?.prompt_id
  const promptIdToUse = currentPromptId || originalFeedbackPromptId
  const matchedPrompt = prompts.find((prompt) => prompt.id === promptIdToUse)

  const agent = `${task?.agent_first_name} ${task?.agent_last_name}`
  const createdBy = task?.created_by
    ? `${task?.created_by_first_name} ${task?.created_by_last_name}`
    : null
  const expectedScore = task?.expected_score
  const feedbackText = task?.description

  const {
    matchingScorecardConfig,
    matchingCriteria,
    mustBePresent,
    scorecardConfigId,
    scorecardConfigSid,
  } = getScorecardLookupData(taskEvidence, scorecardConfigs)

  const {
    editedValues,
    setEditedValue,
    isEditing,
    setIsEditing,
    stagedChanges,
    hasChanges,
    getCurrentPromptValues,
    handleStageChanges,
    handleCancelEdit,
    handleClearStagedChanges,
    stagePrompt,
  } = usePromptEditing(matchedPrompt, task)

  // The Calibration Agent run whose winner is staged, so saving it records the adoption.
  const agentSource = useRef(null)
  useEffect(() => {
    agentSource.current = null
  }, [task?.id])

  const handleUseAgentPrompt = ({ userPrompt, model, optimizationId, candidateId }) => {
    stagePrompt(model ? { userPrompt, model } : { userPrompt })
    agentSource.current = { optimizationId, candidateId }
    toast.success("The agent's prompt is staged — test it, then save or resolve to apply it.")
  }

  const recordAgentAdoption = (newPrompt) => {
    if (agentSource.current && newPrompt?.id) {
      dispatch(recordCalibrationAdopted({ ...agentSource.current, newPromptId: newPrompt.id }))
    }
    agentSource.current = null
  }

  const clearStagedChanges = () => {
    agentSource.current = null
    handleClearStagedChanges()
  }

  // A person's own edit staged after "Use this prompt" makes the prompt theirs, not the agent's.
  const stageOwnChanges = (...args) => {
    agentSource.current = null
    handleStageChanges(...args)
  }

  const {
    testLoading,
    runAllLoading,
    allTestResults,
    expandedTests,
    setExpandedTests,
    handleRunSingleTest,
    handleRunAllTests,
    manualTestCases,
    addManualTestCase,
    removeManualTestCase,
    handleRunManualTest,
    handleRunPromptTest,
  } = usePromptTesting(
    matchedPrompt,
    allEvidence,
    getCurrentPromptValues,
    feedbackText,
    expectedScore,
    task?.id
  )

  const closeDrawer = () => {
    dispatch(setTask(null))
    if (handleClose) {
      handleClose()
    }
  }

  const allPromptIdsToFetch = useMemo(() => {
    const ids = new Set()
    if (promptIdToUse) {
      ids.add(promptIdToUse)
    }
    allEvidence.forEach((evidence) => {
      if (evidence.prompt_id && evidence.prompt_id !== evidence.current_prompt_id) {
        ids.add(evidence.prompt_id)
      }
    })
    return Array.from(ids)
  }, [promptIdToUse, allEvidence])

  useEffect(() => {
    if (!isEmpty(allPromptIdsToFetch)) {
      dispatch(fetchPrompts(allPromptIdsToFetch))
    }
  }, [task?.id, allPromptIdsToFetch.join(',')])

  useEffect(() => {
    if (task?.organization_id && fetchedOrgId !== task.organization_id) {
      if (!matchingScorecardConfig && scorecardConfigId) {
        dispatch(fetchScorecardsByOrg(task.organization_id, 'copilot'))
        setFetchedOrgId(task.organization_id)
      }
    }
  }, [task?.organization_id, scorecardConfigId, matchingScorecardConfig])

  useEffect(() => {
    if (isEmpty(qaCopilotModelOptions)) {
      dispatch(fetchModels())
    }
  }, [])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(hasChanges || !!stagedChanges)
    }
  }, [hasChanges, stagedChanges])

  // Staging an edit is the "started working" signal for the reporting customer.
  // Terminal actions (resolve/dismiss) await inProgressPromiseRef so their status
  // write always lands after this one — otherwise a stage-then-resolve race can
  // leave the task stuck at in_progress and back in the open inbox.
  const inProgressFiredForTask = useRef(null)
  const inProgressPromiseRef = useRef(null)
  useEffect(() => {
    if (
      stagedChanges &&
      [READY_FOR_REVIEW, COLLECTING_EVIDENCE].includes(task?.status) &&
      inProgressFiredForTask.current !== task.id
    ) {
      inProgressFiredForTask.current = task.id
      inProgressPromiseRef.current = dispatch(markTaskInProgress(task.id, task.organization_id))
        .then(() => {
          if (refetchTasks) {
            refetchTasks()
          }
        })
        .catch(() => {
          // Clear the flag so a later stage can retry instead of getting stuck.
          inProgressFiredForTask.current = null
        })
    }
  }, [stagedChanges, task?.status, task?.id])

  const renderFeedbackSubtitle = () => {
    if (isEmpty(allEvidence)) {
      return null
    }

    const subtitle = buildFeedbackSubtitle(
      evidenceCount,
      uniqueCallCount,
      matchingCriteria?.name,
      matchingScorecardConfig?.name,
      matchingScorecardConfig?.id || scorecardConfigId,
      task?.metadata?.organization_name,
      isUnredactedOrg
    )

    if (subtitle.text) {
      return <p className="section-subtitle muted-text">{subtitle.text}</p>
    }

    return (
      <p className="section-subtitle muted-text">
        {subtitle.prefix}
        {subtitle.scorecardName && (
          <ExternalLink
            url={`/qa-copilot/${subtitle.scorecardId}`}
            label={subtitle.scorecardName}
          />
        )}
        {subtitle.orgSuffix}
      </p>
    )
  }

  const promptChangeParams = () => ({
    taskId: task.id,
    taskOrganizationId: task.organization_id,
    promptChanges: stagedChanges,
    originalPrompt: matchedPrompt,
    scorecardConfigId: matchingScorecardConfig?.id || scorecardConfigId,
    scorecardConfigSid,
    criteriaScid: task.scorecard_criteria_scid,
  })

  const handleSaveToScorecard = async () => {
    setSavingToScorecard(true)
    try {
      recordAgentAdoption(await dispatch(savePromptChangesToScorecard(promptChangeParams())))
      handleClearStagedChanges()
      if (refetchTasks) {
        await refetchTasks()
      }
    } catch (error) {
      // Error toast handled in actions
    } finally {
      setSavingToScorecard(false)
    }
  }

  const renderPromptContent = () => {
    if (promptsLoading.prompts) {
      return (
        <div className="empty">
          <Loader label="Loading prompt..." />
        </div>
      )
    }

    if (matchedPrompt) {
      return (
        <PromptEditor
          prompt={matchedPrompt}
          editedValues={editedValues}
          setEditedValue={setEditedValue}
          stagedChanges={stagedChanges}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          hasChanges={hasChanges}
          handleStageChanges={stageOwnChanges}
          handleCancelEdit={handleCancelEdit}
          onClearStagedChanges={clearStagedChanges}
          onSaveToScorecard={handleSaveToScorecard}
          savingToScorecard={savingToScorecard}
          qaCopilotModelOptions={qaCopilotModelOptions}
          canEdit={!isUnredactedOrg}
        />
      )
    }

    if (promptIdToUse) {
      return <p className="muted-text">Unable to load prompt (ID: {promptIdToUse})</p>
    }

    return <p className="muted-text">No prompt associated with this task</p>
  }

  const handleResolve = async () => {
    try {
      if (inProgressPromiseRef.current) {
        await inProgressPromiseRef.current
      }
      if (stagedChanges) {
        recordAgentAdoption(await dispatch(resolveWithPromptChanges(promptChangeParams())))
      } else {
        await dispatch(patchTask(task.id, { status: COMPLETED }, null, task.organization_id))
      }
      if (refetchTasks) {
        await refetchTasks()
      }
      closeDrawer()
    } catch (error) {
      // Error toast handled in actions
    }
  }

  const wrappedPrimaryAction = () => handlePrimaryAction(task?.id, closeDrawer)
  const wrappedSecondaryAction = handleSecondaryAction
    ? async () => {
        if (inProgressPromiseRef.current) {
          await inProgressPromiseRef.current
        }
        return handleSecondaryAction(task?.id, closeDrawer)
      }
    : null

  const getPrimaryAction = () => {
    if (isUnredactedOrg) {
      return wrappedSecondaryAction
    }
    if (taskIsOpen) {
      return handleResolve
    }
    return wrappedPrimaryAction
  }

  const getPrimaryButtonText = () => {
    if (isUnredactedOrg) {
      return secondaryActionButtonText || 'Dismiss'
    }
    if (stagedChanges) {
      return 'Close Task and Apply to Scorecard'
    }
    return primaryActionButtonText
  }

  const feedbackSection = (
    <section className="prompt-feedback-drawer__section">
      <div className="section-header">
        <div className="flex-align-center small-gap">
          <h3 className="no-margin">Feedback</h3>
        </div>
        {renderFeedbackSubtitle()}
      </div>

      <EvidenceList
        allEvidence={allEvidence}
        expectedScore={expectedScore}
        feedbackText={feedbackText}
        prompts={prompts}
        fallbackAgentName={agent}
      />

      {isEmpty(allEvidence) && task?.call_id && (
        <div className="reference-links mt">
          <div className="reference-item">
            <span className="muted-text">Call: </span>
            <ExternalLink url={`/calls/${task.call_id}`} label={task.call_id} />
            <CopyButton content={toString(task.call_id)} showTooltip iconOnly />
          </div>
        </div>
      )}
    </section>
  )

  const promptSection = (
    <section className="prompt-feedback-drawer__section">
      <h3>Prompt</h3>

      {renderPromptContent()}
    </section>
  )

  const testSection = !isUnredactedOrg && (
    <section className="prompt-feedback-drawer__section" data-testid="prompt-feedback-test-section">
      <div className="section-header">
        <div className="flex-space-between flex-align-center">
          <div className="flex-align-center small-gap">
            <h3 className="no-margin">Feedback</h3>
          </div>
          <TestSectionHeader
            prompt={matchedPrompt}
            allEvidence={allEvidence}
            allTestResults={allTestResults}
            runAllLoading={runAllLoading}
            handleRunAllTests={handleRunAllTests}
            manualTestCases={manualTestCases}
            expandedTests={expandedTests}
            setExpandedTests={setExpandedTests}
          />
        </div>
        {renderFeedbackSubtitle()}
      </div>

      {isEmpty(allEvidence) && task?.call_id && (
        <div className="reference-links mb">
          <div className="reference-item">
            <span className="muted-text">Call: </span>
            <ExternalLink url={`/calls/${task.call_id}`} label={task.call_id} />
            <CopyButton content={toString(task.call_id)} showTooltip iconOnly />
          </div>
        </div>
      )}

      <CriteriaContext matchingCriteria={matchingCriteria} mustBePresent={mustBePresent} />

      <TestCaseList
        allEvidence={allEvidence}
        dismissedEvidence={dismissedEvidence}
        prompt={matchedPrompt}
        expectedScore={expectedScore}
        feedbackText={feedbackText}
        allTestResults={allTestResults}
        expandedTests={expandedTests}
        setExpandedTests={setExpandedTests}
        testLoading={testLoading}
        runAllLoading={runAllLoading}
        handleRunSingleTest={handleRunSingleTest}
        mustBePresent={mustBePresent}
        manualTestCases={manualTestCases}
        addManualTestCase={addManualTestCase}
        removeManualTestCase={removeManualTestCase}
        handleRunManualTest={handleRunManualTest}
        handleRunPromptTest={handleRunPromptTest}
        criteriaScid={task?.scorecard_criteria_scid}
        taskId={task?.id}
        organizationId={task?.organization_id}
        canEdit={!isUnredactedOrg}
      />
    </section>
  )

  const optimizationSection = !isUnredactedOrg && task && (
    <OptimizationReport
      key={task.id}
      task={task}
      onUsePrompt={matchedPrompt ? handleUseAgentPrompt : null}
      canAct={taskIsOpen}
      currentPromptText={matchedPrompt?.user_prompt}
      hasStagedChanges={!isNil(stagedChanges) && isNil(agentSource.current)}
      isEditingPrompt={isEditing && hasChanges}
      mustBePresent={mustBePresent}
    />
  )

  const commentsSection = (
    <section className="prompt-feedback-drawer__section">
      <h3 className="flex-align-center small-gap">
        <IconMessageCircle className="icon-svg" />
        Comments
      </h3>
      <TaskComments
        taskId={task?.id}
        organizationId={task?.organization_id}
        taskType={task?.type}
      />
    </section>
  )

  const footer = (
    <TaskDrawerFooter
      handlePrimaryAction={getPrimaryAction()}
      handleSecondaryAction={isUnredactedOrg ? null : wrappedSecondaryAction}
      primaryActionButtonText={getPrimaryButtonText()}
      secondaryActionButtonText={isUnredactedOrg ? null : secondaryActionButtonText}
    />
  )

  return (
    <div
      className={classNames('prompt-feedback-drawer', {
        'is-expanded': expanded,
        'flex-column': expanded,
        'full-height': expanded,
      })}
      data-testid="prompt-feedback-drawer"
    >
      {!expanded && (
        <>
          <h2>{task?.name}</h2>

          <Metadata
            agent={agent}
            createdAt={task?.created_at}
            createdBy={createdBy}
            isAiGenerated={task?.ai_generated}
            type={task?.type}
            status={task?.status}
            agentReviewed={task?.agent_reviewed}
          />
        </>
      )}

      {isUnredactedOrg && (
        <Banner info className="mb">
          This task belongs to a protected organization. To edit prompts, log into{' '}
          <strong>{task?.metadata?.organization_name || 'the organization'}</strong> directly.
        </Banner>
      )}

      <div className="prompt-feedback-drawer__content">
        {expanded ? (
          <div className="prompt-feedback-drawer__columns flex flex-item gap">
            <div className="prompt-feedback-drawer__left-pane">
              {promptSection}
              {optimizationSection}
            </div>
            <div className="prompt-feedback-drawer__right-pane">
              {isUnredactedOrg && feedbackSection}
              {testSection}
              {commentsSection}
            </div>
          </div>
        ) : (
          <>
            {isUnredactedOrg && feedbackSection}
            {promptSection}
            {optimizationSection}
            {testSection}
            {commentsSection}
          </>
        )}

        {footer}
      </div>
    </div>
  )
}
